import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, KeyRound, Loader2, ShieldCheck, CheckCircle2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CustomerResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showA, setShowA] = useState(false);
  const [showB, setShowB] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const validation = useMemo(() => {
    if (!pw) return { ok: false, msg: '' };
    if (pw.length < 8) return { ok: false, msg: 'Use at least 8 characters.' };
    if (confirm && confirm !== pw) return { ok: false, msg: "Passwords don't match." };
    return { ok: true, msg: confirm ? 'Looks good.' : 'Confirm to continue.' };
  }, [pw, confirm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) { setError('Missing token. Please use the latest reset email.'); return; }
    if (!validation.ok || !confirm) { setError(validation.msg || 'Complete both password fields.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/customers/auth/reset-password`, { token, new_password: pw });
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not reset password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-160px)] flex items-center justify-center px-4 py-16" style={{ backgroundColor: '#f7f9f3' }} data-testid="customer-reset-page">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-emerald-100 overflow-hidden">
        <div className="px-8 py-6 text-white" style={{ background: 'linear-gradient(135deg,#2d5016 0%,#3a6b1d 100%)' }}>
          <div className="flex items-center gap-3"><ShieldCheck className="w-6 h-6" /><div className="text-xl font-semibold">Choose a new password</div></div>
        </div>

        {success ? (
          <div className="px-8 py-10 text-center" data-testid="customer-reset-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
            <p className="text-sm text-slate-700 mb-2">Password updated.</p>
            <p className="text-xs text-slate-500 mb-4">Redirecting to sign-in\u2026</p>
            <Link to="/login" className="text-emerald-700 hover:underline">Go to sign-in</Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-8 py-7 space-y-5" data-testid="customer-reset-form">
            <p className="text-sm text-slate-600">Pick a new password (minimum 8 characters). The reset link expires in 1 hour and is single-use.</p>
            {[
              { label: 'New password', val: pw, set: setPw, show: showA, toggle: () => setShowA(v => !v), tid: 'customer-reset-new' },
              { label: 'Confirm password', val: confirm, set: setConfirm, show: showB, toggle: () => setShowB(v => !v), tid: 'customer-reset-confirm' },
            ].map((f) => (
              <div key={f.tid}>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{f.label}</label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                  <input type={f.show ? 'text' : 'password'} value={f.val} onChange={(e) => f.set(e.target.value)} required disabled={submitting} data-testid={f.tid} className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600" />
                  <button type="button" onClick={f.toggle} className="absolute right-2 top-2.5 p-1 text-slate-500 hover:text-slate-700" aria-label={f.show ? 'Hide password' : 'Show password'}>{f.show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                </div>
              </div>
            ))}
            {pw && <p className={`text-xs ${validation.ok ? 'text-emerald-700' : 'text-amber-700'}`}>{validation.msg}</p>}
            {error && <div className="text-sm rounded-lg px-3 py-2.5 bg-red-50 border border-red-200 text-red-700 flex items-start gap-2" data-testid="customer-reset-error"><AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{error}</span></div>}
            <button type="submit" disabled={submitting || !validation.ok || !confirm} data-testid="customer-reset-submit" className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-white font-medium transition disabled:opacity-60" style={{ background: 'linear-gradient(135deg,#2d5016 0%,#3a6b1d 100%)' }}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              {submitting ? 'Updating\u2026' : 'Update password'}
            </button>
            <p className="text-xs text-slate-500 text-center"><Link to="/login" className="text-emerald-700 hover:underline">Back to sign-in</Link></p>
          </form>
        )}
      </motion.div>
    </div>
  );
}
